//@ts-check
/**
 * Constants for RCS Level-of-Detail (LOD) management and related configuration.
 */

/**
 * LOD mapping mode:
 * - 'threshold': LOD levels based on point count thresholds (uses RCS_LOD_THRESHOLDS)
 * - 'direct': Direct mapping from source octree levels to target LOD levels
 *
 * In 'direct' mode the number of coarsest source levels combined into LOD 0 is
 * chosen per-model at load time (see {@link ./RcsLodMapping.js#computeDirectModeLevelsInLod0}).
 * With `levelsInLod0`:
 *   LOD 0 = source levels 0 to (levelsInLod0 - 1) (combined)
 *   LOD 1 = source level levelsInLod0
 *   LOD 2 = source level levelsInLod0 + 1
 *   etc.
 *
 * In 'threshold' mode:
 *   LOD levels determined by cumulative point counts
 *
 * @type {'threshold'|'direct'}
 */
export const LOD_MAPPING_MODE = 'direct';

/**
 * Floats a single decoded point occupies in a vertex buffer: interleaved XYZRGB
 * (see RcsFileLoader._createPointGeometry's vblayout).
 * @type {number}
 */
export const RCS_FLOATS_PER_POINT = 6;

/**
 * Number of bytes a single decoded point occupies in memory.
 * Used to translate point counts into an in-memory footprint when auto-selecting
 * the coarsest LOD-0 level range.
 * @type {number}
 */
export const RCS_BYTES_PER_POINT = RCS_FLOATS_PER_POINT * 4;

/**
 * Upper bound on the in-memory footprint of the initially loaded / pulled-up LOD-0
 * dataset in 'direct' mode. {@link ./RcsLodMapping.js#computeDirectModeLevelsInLod0}
 * combines the largest number of coarsest source levels into LOD 0 whose footprint
 * stays within this cap (always keeping at least {@link MIN_LEVELS_IN_LOD0} level).
 * Maximizing under the cap naturally pulls up as much as fits — in practice landing
 * around 20-50 MB for typical datasets — without ever exceeding it. There is no
 * separate enforced lower bound: for a small model the whole cloud may fit under the
 * cap, and a model whose next level would overshoot the cap simply stays below it.
 * @type {number}
 */
export let RCS_LOD0_TARGET_MAX_BYTES = 250 * 1024 * 1024;

/**
 * Override {@link RCS_LOD0_TARGET_MAX_BYTES}. Must be called before the model is loaded,
 * since the LOD-0 level count is decided at load time. Used by tests to exercise different
 * byte budgets without changing the shipped default.
 * @param {number} maxBytes
 */
export function setRcsLod0TargetMaxBytes(maxBytes) {
  if (typeof maxBytes !== 'number' || !Number.isFinite(maxBytes) || maxBytes <= 0) {
    throw new Error(`Invalid RCS_LOD0_TARGET_MAX_BYTES: ${maxBytes}. Must be a positive finite number.`);
  }
  RCS_LOD0_TARGET_MAX_BYTES = maxBytes;
}

/**
 * Point count thresholds for each LOD level (used in 'threshold' mode)
 * Each entry defines the minimum point count for that LOD level
 *
 * Example: [1000, 10_000, 30_000, Infinity]
 * - LOD 0: 0 to 1,000 points (lowest detail)
 * - LOD 1: 1,000 to 10,000 points (medium detail)
 * - LOD 2: 10,000 to 30,000 points (high detail)
 * - LOD 3: 30,000+ points (highest detail)
 */
export const RCS_LOD_THRESHOLDS = [300, 1000, 3000, 6000, 10_000, 15_000, 30_000, 60_000, 90_000, Infinity];

/**
 * Maximum number of octree levels stored per umbrella node in the RCS spec.
 * @type {number}
 */
export const RCS_MAX_OCTREE_LEVELS = 32;

/**
 * Smallest number of source levels that can be combined into LOD 0 in 'direct' mode.
 * LOD 0 must always contain at least source level 0, so this is the floor returned by
 * {@link ./RcsLodMapping.js#computeDirectModeLevelsInLod0}.
 * @type {number}
 */
export const MIN_LEVELS_IN_LOD0 = 1;

/**
 * Stride for packing (umbrellaIndex, lodLevel) into a single numeric `fragmentIdMap` key
 * (`umbrellaIndex * MAX_LOD_LEVELS + lodLevel`). Must be strictly greater than the maximum
 * LOD level index that {@link ./RcsLodMapping.js#getRequiredLodLevels getRequiredLodLevels} can return.
 *
 * In 'direct' mode the number of source levels folded into LOD 0 is chosen per-model at
 * load time, so this stride is a fixed upper bound derived from the smallest possible
 * `levelsInLod0` ({@link MIN_LEVELS_IN_LOD0}): the max LOD index is
 * `RCS_MAX_OCTREE_LEVELS - MIN_LEVELS_IN_LOD0`. In 'threshold' mode it is
 * `RCS_LOD_THRESHOLDS.length - 1`. We add 1 to convert the max LOD index into the number
 * of distinct LOD levels (indices 0..max inclusive). Keeping this constant (not per-model)
 * ensures fragment-id keys are packed consistently across all call sites.
 * @type {number}
 */
export const MAX_LOD_LEVELS =
(LOD_MAPPING_MODE === 'direct' ?
RCS_MAX_OCTREE_LEVELS - MIN_LEVELS_IN_LOD0 :
RCS_LOD_THRESHOLDS.length - 1) +
1;

/**
 * This factor determines how we scale the feature size derived from the point cloud
 * resolution relative to object size. This will impact both small feature culling and
 * LOD selection when compared to meshes.
 *
 * For meshes we use the size of the largest object in the node as reference to decide on
 * priority. For points, we don't have a largest object. This factor determines how to map
 * the feature size of 0.5 ^ level * nodeSize to a feature size for the sake of comparision.
 *
 * Making this too small will cause nodes to be small feature culled.
 *
 * TODO: Improve the computation of this factor. It currently depends on node size and level.
 *       for inner nodes the node size still decreases, but for leaves it stays the same. So
 *       the computation is inconsistent, we should turn this into a reliable geometric
 *       measure.
 */
export const FEATURE_SIZE_SCALING_FACTOR = 20;

/**
 * Maximum number of parallel in-flight requests to the RCS server
 */
export const RCS_NUM_IN_FLIGHT_REQUESTS = 3000;

/**
 * Reduced async promotion limit for localhost direct-file loads (dev server only).
 * Loading RCS from localhost is for debugging; the HTTP/1.1 dev server cannot
 * sustain thousands of concurrent range requests.
 */
export const RCS_NUM_IN_FLIGHT_REQUESTS_HTTP = 300;

/**
 * Proxy Configuration
 *
 * Ranger HTTP Range Request Proxy (Rust/Axum)
 *  - GET with Range header containing comma-separated ranges
 *  - Returns standard RFC 7233 multipart/byteranges response
 *  - Example: 'http://localhost:8888'
 *
 * @type {string|null} Proxy URL or null to disable proxy
 */
//export const HTTP2_PROXY_URL = null;
export let HTTP2_PROXY_URL = 'https://localhost:8888';

/**
 * Proxy type: 'ranger', 'ranger_bin', or 'ranger_ws'
 * - 'ranger': RFC 7233 compliant multipart/byteranges proxy
 * - 'ranger_bin': Binary protocol over HTTP POST
 * - 'ranger_ws': WebSocket-based binary protocol for persistent connections on localhost
 * - 'ranger_ws_remote': WebSocket-based binary protocol for persistent connections to remote server
 * - 'ranger_ws_remote_old': WebSocket-based binary protocol for persistent connections to remote server using the old ranger poc backend
 * @type {'ranger'|'ranger_bin'|'ranger_ws'|'ranger_ws_remote'|'ranger_ws_remote_old'}
 */
export const PROXY_TYPE = 'ranger_ws_remote';

// @ts-ignore PROXY_TYPE is a build-time constant; comparison supports ranger_ws_remote_old override
export const USE_OLD_RANGER = PROXY_TYPE === 'ranger_ws_remote_old';

/**
 * Maximum number of range requests to batch in a single proxy request
 * @type {number}
 */
export const PROXY_BATCH_SIZE = 30;

/**
 * Maximum time to wait (in milliseconds) before sending a partial batch
 * @type {number}
 */
export const PROXY_BATCH_TIMEOUT_MS = 30;

/**
 * Maximum total bytes to accumulate before immediately flushing the batch
 * This prevents large requests from waiting for the timeout or batch size limit
 * @type {number}
 */
export const PROXY_BATCH_BYTES_THRESHOLD = 1000 * 1024; // ~1 MB

/**
 * Instance tree detail level options:
 * - 'simple': Single flat tree with all fragments sharing one dbId (dbId = 1)
 * - 'debugOctree': DEBUGGING ONLY — hierarchical tree with Root → LOD Levels → Individual Nodes (dbId = fragId)
 *
 * This is a live binding consumed by the loader at model-load time, so it can be
 * overridden via {@link setInstanceTreeMode} before loading a model (e.g. from tests
 * that need a specific layout regardless of the shipped default).
 * @type {'simple'|'debugOctree'}
 */
export let INSTANCE_TREE_MODE = 'simple';

/**
 * Override {@link INSTANCE_TREE_MODE}. Must be called before the model is loaded,
 * since the instance tree layout and fragment→dbId mapping are decided at load time.
 * @param {'simple'|'debugOctree'} mode
 */
export function setInstanceTreeMode(mode) {
  if (mode !== 'simple' && mode !== 'debugOctree') {
    throw new Error(`Invalid INSTANCE_TREE_MODE: ${mode}. Must be 'simple' or 'debugOctree'.`);
  }
  INSTANCE_TREE_MODE = mode;
}

/**
 * Stable viewableID for the default RCS geometry viewable.
 * Shared by every RCS file so the ID does not change between loads.
 * @type {string}
 */
export const RCS_DEFAULT_VIEWABLE_ID = 'aa85aad6-c480-4a35-9cbf-4cf5994a25ba';
/**
 * Stable name for the default RCS geometry viewable.
 * @type {string}
 */
export const RCS_DEFAULT_VIEWABLE_NAME = 'Point Cloud';

/**
 * OPFS cache schema version. Changing this value invalidates all cached RCS data,
 * forcing a fresh load from the network. Bump when the caching format or
 * algorithm parameters change in a way that makes old cached data invalid.
 * @type {number}
 */
export const RCS_CACHE_VERSION = 1;

/**
 * Points-per-level target used in _convertOctreeNodeToBinary for distributing
 * coarsest points to parent inner nodes. Included in cache params hash so that
 * changing this value invalidates the inner-node cache.
 * @type {number}
 */
export const POINTS_PER_LEVEL = 100_000;

/**
 * RCS on-disk file header size in bytes (fixed by the file format; includes the
 * chunk directory).
 * @type {number}
 */
export const RCS_HEADER_BYTES = 98605;

/**
 * Magic string at the start of the RCS file header, stored in an 8-byte null-padded field.
 * @type {string}
 */
export const RCS_HEADER_MAGIC = 'ADOCT';

/**
 * Umbrella octree leaf record size in chunk 2 (bytes), fixed by the file format.
 * @type {number}
 */
export const UMBRELLA_LEAF_RECORD_BYTES = 368;

/**
 * Size of the little-endian uint64 leaf count that prefixes umbrella octree chunk 2,
 * ahead of the {@link UMBRELLA_LEAF_RECORD_BYTES}-sized leaf records. Fixed by the file format.
 * @type {number}
 */
export const OCTREE_CHUNK_LEAF_COUNT_BYTES = 8;

/**
 * Raw on-disk size of a single SVO point in the voxel chunk, fixed by the file format.
 * @type {number}
 */
export const RCS_BYTES_PER_SVO_POINT = 16;

/**
 * OPFS metadata stride per cached blob entry: a 20-byte hash plus a 4-byte blob size.
 * Mirrors `METADATA_STRIDE` in lmvtk/otg/OPFSCache.ts, which is module-private there.
 * @type {number}
 */
export const OPFS_METADATA_BYTES_PER_ENTRY = 24;